import type React from 'react';
import { useState } from 'react';
import AssistanceTypeChart from './AssistanceTypeChart';
import { districtBreakdownTableColumns } from './columns.districtBreakdownTable';
import DistrictBreakdownTable from './DistrictBreakdownTable';
import DistrictFilter from './DistrictFilter';
import type { MKEERAApiResponseItem } from './dashboard.types';
import GenderChart from './GenderChart';
import { useAssistanceTypeChartData } from './hooks/useAssistanceTypeChartData';
import { useAverageAndHighestAssistance } from './hooks/useAverageAndHighestAssistance';
import { useDistrictBreakdownTableData } from './hooks/useDistrictBreakdownTableData';
import { useMapMarkers } from './hooks/useMapMarkers';
import { useMilwaukeeansServedChartData } from './hooks/useMilwaukeeansServedChartData';
import { usePayeeTypeChartData } from './hooks/usePayeeTypeChartData';
import { usePaymentsChartData } from './hooks/usePaymentsChartData';
import { useUtilityPayments } from './hooks/useUtilityPayments';
import MapSection from './MapSection';
import MilwaukeeansServed from './MilwaukeeansServed';
import PayeeTypeChart from './PayeeTypeChart';
import StatCard from './StatCard';
import TotalPayments from './TotalPayments';

const UtilityPaymentsCard: React.FC<{ value: number; className?: string }> = ({ value, className }) => (
  <StatCard className={`${className ?? ''} flex-row justify-between items-center`}>
    <span className='text-lg font-medium text-neutralColor-darkest'>Utility Payments</span>
    <span className='text-2xl font-bold text-primaryColor-normal'>
      {value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      })}
    </span>
  </StatCard>
);

const Dashboard: React.FC<{
  data: MKEERAApiResponseItem[];
  totalRecords: number;
}> = ({ data = [], totalRecords = 0 }) => {
  // Safety check: ensure data is an array and handle potential undefined/null values
  const safeData = Array.isArray(data) ? data : [];
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');

  // All hooks now have their own safety checks, but we ensure they receive valid data
  const paymentsChartData = usePaymentsChartData(safeData);
  const milwaukeeansServedChartData = useMilwaukeeansServedChartData(safeData, totalRecords);
  const mapMarkers = useMapMarkers(safeData);
  const utilityPayments = useUtilityPayments(safeData);
  const averageAndHighest = useAverageAndHighestAssistance(safeData);
  const payeeType = usePayeeTypeChartData({ data: safeData, selectedDistrict });
  const assistanceType = useAssistanceTypeChartData({
    data: safeData,
    selectedDistrict,
  });
  const districtBreakdownTableData = useDistrictBreakdownTableData(safeData);

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 bg-neutralColor-lightest [--dashboard-gap:1rem] [--dashboard-padding:1.5rem] gap-[var(--dashboard-gap)] [--dashboard-radius:2px]'>
      {/* Top stats */}
      <div className='col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 items-stretch gap-[var(--dashboard-gap)]'>
        <div>
          <TotalPayments data={paymentsChartData} className='h-full p-[var(--dashboard-padding)]' />
        </div>
        <div>
          <MilwaukeeansServed data={milwaukeeansServedChartData} className='h-full p-[var(--dashboard-padding)]' />
        </div>
      </div>

      {/* Map */}
      <div className='md:col-span-2 shadow rounded-[var(--dashboard-radius)]'>
        <MapSection markers={mapMarkers} className='p-[var(--dashboard-padding)]' />
      </div>

      {/* Utility Payments & Gender */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-[var(--dashboard-gap)] md:col-span-2'>
        <UtilityPaymentsCard value={utilityPayments} className='h-full p-[var(--dashboard-padding)]' />
        <GenderChart data={safeData} className='h-full p-[var(--dashboard-padding)]' />
      </div>
      {/* Average Assistance & Highest Assistance */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-[var(--dashboard-gap)] md:col-span-2'>
        <StatCard className='h-full p-[var(--dashboard-padding)] flex-row justify-between items-center'>
          <div>
            <div className='text-base font-medium text-neutralColor-darkest'>Average Assistance</div>
          </div>
          <div className='text-2xl font-bold text-primaryColor-normal'>
            {averageAndHighest.average.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
            })}
          </div>
        </StatCard>
        <StatCard className='h-full p-[var(--dashboard-padding)] flex-row justify-between items-center'>
          <div>
            <div className='text-base font-medium text-neutralColor-darkest'>Highest Assistance Provided</div>
          </div>
          <div className='text-2xl font-bold text-primaryColor-normal'>
            {averageAndHighest.highest.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
            })}
          </div>
        </StatCard>
      </div>

      {/* District Filter */}
      <div className='bg-white shadow rounded-[var(--dashboard-radius)]'>
        <div className='p-[var(--dashboard-padding)]'>
          <div className='flex items-center gap-4 '>
            <span className='text-lg font-medium text-neutralColor-darkest whitespace-nowrap'>Filter by District:</span>
            <DistrictFilter className='w-full' data={data} selectedDistrict={selectedDistrict} onDistrictChange={setSelectedDistrict} />
          </div>
        </div>
      </div>

      {/* Empty placeholder */}
      <div />

      {/* Pie charts */}
      <div className='h-full bg-white shadow'>
        <PayeeTypeChart entries={payeeType.entries} />
      </div>
      <div className='h-full bg-white shadow'>
        <AssistanceTypeChart entries={assistanceType.entries} />
      </div>

      {/* Table and Image Side by Side */}
      <div className='bg-white shadow rounded-[var(--dashboard-radius)]'>
        <DistrictBreakdownTable data={districtBreakdownTableData} columns={districtBreakdownTableColumns} className='h-full' />
      </div>
      <div className='h-[848px]'>
        <img
          src='https://stp-public-marketing-videos.s3.us-east-2.amazonaws.com/map/map.png'
          alt='Milwaukee Districts Map'
          className='w-auto h-full object-cover rounded-[var(--dashboard-radius)] aspect-auto'
        />
      </div>
    </div>
  );
};

export default Dashboard;
